<script setup>
import { computed, ref, getCurrentInstance, onMounted, onUnmounted } from 'vue'
import StreamService from '@/services/StreamService';
import CountDown from '../CountDown.vue'
import FormButton from '../../Atoms/FormButton.vue'
import WebcamPlayer from '../WebcamPlayer.vue'
import StreamPlayer from '../StreamPlayer.vue'
import StreamPlayerHls from '../StreamPlayer.hls.vue'
import { useStore } from 'vuex'
import Tooltip from '../../Atoms/Tooltip.vue'
import { useChatOnlyLock } from '@/composables/useChatOnlyLock'

const props = defineProps({
  stream: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id');
    }
  },
  mediaPulse: {
    type: Object,
    default() { return {}; }
  },
  streamPreviewSpecs: {
    type: Object,
    default() { return {}; }
  },
  streamAlive: {
    type: Boolean
  },
  streamSourceType: {
    type: String
  },
  sourceTypes: {
    type: Object
  },
  cannotToggleStream: {
    type: Boolean
  },
  transcoderView: {
    type: Boolean
  },
  streamCountdownSecLeft: {
    type: Object
  },
  countdownDate: {
    type: Object
  },
  audioDevice: {
    type: Object,
    default: null
  },
  videoDevice: {
    type: Object,
    default: null
  }
})

const store = useStore()

const webcamDevices = ref(null)
const countdownDate = ref(null)
const streamCountdownSecLeft = ref(null)

const userEmail = computed(() => store.state.User.email)

const { locked: chatOnlyLocked, tooltip: chatOnlyTooltip } = useChatOnlyLock(
  computed(() => props.stream?.type === 'live'),
)

onMounted(async () => {
  webcamDevices.value = await navigator.mediaDevices?.enumerateDevices();
})

const emit = defineEmits(['show-stream-toggle-modal'])

const showStreamToggleModal = (event) => { 
  emit('show-stream-toggle-modal')
}

const isPortraitResolution = computed(() => {
  if (props.streamAlive && props.mediaPulse && Number(props.mediaPulse.width) < Number(props.mediaPulse.height)) {
    return true
  }

  return false
})

</script>
<template>
  <div 
    class="video-wrapper max-w-[100vw]"
    :style="{
      backgroundColor: isPortraitResolution ? 'var(--surface-2)' : '#000'
    }"
  >
    <webcam-player v-if="streamSourceType === sourceTypes.Webcam" :stream="stream" :audioDevice="audioDevice"
      :videoDevice="videoDevice" class="video-thumb" @stream-stopped="onWebcamStopped" @stream-started="onWebcamStarted"
      @stream-authorized="onWebcamAuthorized" />
    <div v-else-if="!stream.enabled" class="video-thumb placeholder text-center">
      <p class="text-center text-xl text-surface-7 font-medium mb-1">
        Disabled Stream
      </p>
      <p class="text-sm text-surface-7">Please enable</p>
      <!-- The preview deliberately sits outside StreamSidebar's chat-only lock so a
           moderator can still watch the stream, which leaves this one control to
           handle here rather than rendering it enabled over a no-op click. -->
      <Tooltip placement="top" :active="cannotToggleStream || chatOnlyLocked" :wrap-trigger="chatOnlyLocked">
        <FormButton @click="showStreamToggleModal" size="sm" class="mt-4"
          :disabled="chatOnlyLocked || cannotToggleStream">
          Enable Stream
        </FormButton>
        <template #content>
          <span v-if="chatOnlyLocked" class="text-surface-8 text-small">{{ chatOnlyTooltip }}</span>
          <span v-else class="text-surface-8 text-small">
            You don't have permission. Contact the team owner at <a class="text-surface-8 text-small"
              :href="'mailto:' + userEmail">{{ userEmail }}</a> to gain access.</span>
        </template>
      </Tooltip>
    </div>
    <div v-else-if="!streamAlive" class="video-thumb placeholder">
      <p v-if="streamType === 'ipcam'">No camera source</p>
      <p v-else-if="countdownDate && streamCountdownSecLeft"
        class="flex justify-center items-center flex-col">
        Stream will go live in <br>
        <CountDown :endDate="countdownDate" />
      </p>
      <div class="m-0 flex flex-col items-center text-center px-6" v-else>
        <p class="text-surface-8 font-medium mb-1">Waiting for stream</p>
        <p class="text-tiny text-surface-6 mb-0">Preview will appear once your encoder connects.</p>
      </div>
    </div>

    <stream-player v-else-if="streamPreviewSpecs.type === 'mse'" :stream="stream" :stream-url="streamPreviewSpecs.url"
      :media-pulse="mediaPulse" class="video-thumb" />

    <stream-player-hls v-else-if="streamPreviewSpecs.type === 'hls'" :stream="stream"
      :stream-url="streamPreviewSpecs.url" :autoplay="true" :frameless="true" class="video-thumb" />
  </div>
</template>